import {Box, Grid, MenuItem} from '@material-ui/core'
import User from 'src/models/User'
import ButtonMedium from '../Buttons/ButtonMedium'
import {useState} from 'react'
import PasswordDialog from './PasswordDialog'
import {FormContainer} from './styled'
import TextInput from '../TextInput'
import {useEffect} from 'react'
import EditTemplate from '../EditTemplate'

export type SingleUserProps = {
  editUser?: User
  title: string
  isSuccess?: boolean
  isBusy: boolean
  errorMessage?: string
  onSaveUser: (user: User) => void
  onCancel: () => void
  onDismissSuccess: () => void
}

const isAdminOptions = [
  {value: 1, label: 'Yes'},
  {value: 0, label: 'No'},
]

const isEmployeeOptions = [
  {value: 1, label: 'Yes'},
  {value: 0, label: 'No'},
]

const blankUser: User = {
  email: '',
  name: '',
  password: undefined,
  isAdmin: false,
  isEmployee: false,
  id: undefined,
  token: undefined,
}

const SingleUser: React.FC<SingleUserProps> = ({
  title,
  editUser,
  isSuccess,
  isBusy,
  errorMessage,
  onSaveUser,
  onCancel,
  onDismissSuccess,
}: SingleUserProps) => {
  const [errorString, setErrorString] = useState<string | undefined>()
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [user, setUser] = useState<User>(blankUser)

  const isEditUser: boolean = editUser !== undefined

  // Set the user in the state if we're editing.
  useEffect(() => {
    if (editUser) {
      setUser(editUser)
    }
  }, [editUser])

  // Text to show in the password button
  const passwordButtonText = isEditUser ? 'Change Password' : 'Set Password'

  // When the use closes the password dialog (new password provided if changed)
  const onChangePasswordClose = (newPassword?: string) => {
    // Close the dialog.
    setPasswordDialogOpen(false)

    // Save the password
    if (newPassword) {
      setUser({...user, password: newPassword})
    }
  }

  ///
  /// Called when the save button is clicked.
  ///
  const onSaveClick = () => {
    setErrorString(undefined)

    if (!isEditUser && !user.password) {
      // This is a new user, but no password is set.
      setPasswordDialogOpen(true)
    } else {
      if (!user.email) {
        setErrorString('Please provide an email address')
      } else if (!user.name) {
        setErrorString('Please provide your full name')
      } else {
        // Need to save this user.
        onSaveUser(user)
      }
    }
  }

  const successString = isSuccess ? (isEditUser ? 'User Updated!' : 'User Created') : undefined

  return (
    <Box>
      <EditTemplate
        title={title}
        busyString={isBusy ? 'Please Wait...' : undefined}
        errorMessage={errorString ? errorString : errorMessage ? errorMessage : undefined}
        successString={successString}
        allFieldsRequired
        onSave={onSaveClick}
        onCancel={onCancel}
        onDismissSuccess={onDismissSuccess}>
        <FormContainer border={1} borderColor="text.secondary" borderRadius={10}>
          <Grid container spacing={5} xs={12} alignItems="center" justify="center">
            <Grid item xs={6}>
              <TextInput
                fullWidth={true}
                id="email"
                label="E-Mail"
                value={user.email}
                onChangeText={(email: string) => {
                  setUser({...user, email})
                }}
                onSubmitEditing={onSaveClick}
              />
            </Grid>

            <Grid item xs={6}>
              <TextInput
                fullWidth={true}
                id="full_name"
                label="Full Name"
                value={user.name}
                onChangeText={(fullName: string) => {
                  setUser({...user, name: fullName})
                }}
                onSubmitEditing={onSaveClick}
              />
            </Grid>

            <Grid item xs={3} />

            <Grid item xs={6}>
              <Box display="flex" justifyContent="center" alignItems="center">
                <ButtonMedium color={'secondary'} onClick={() => setPasswordDialogOpen(true)}>
                  {passwordButtonText}
                </ButtonMedium>
              </Box>
            </Grid>

            <Grid item xs={3} />

            <Grid item xs={6}>
              <TextInput
                select
                value={user.isAdmin ? 1 : 0}
                fullWidth={true}
                id="isAdmin"
                label="CMS Admin?"
                onChangeText={(text: string) => {
                  setUser({...user, isAdmin: JSON.stringify(text) === '1'})
                }}
                onSubmitEditing={onSaveClick}>
                {isAdminOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextInput>
            </Grid>

            <Grid item xs={6}>
              <TextInput
                select
                value={user.isEmployee ? 1 : 0}
                fullWidth={true}
                id="isEmployee"
                label="ReactPT Employee?"
                onChangeText={(text) => {
                  setUser({...user, isEmployee: JSON.stringify(text) === '1'})
                }}
                onSubmitEditing={onSaveClick}>
                {isEmployeeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextInput>
            </Grid>
          </Grid>
        </FormContainer>
        <PasswordDialog
          title={passwordButtonText}
          onClose={onChangePasswordClose}
          open={passwordDialogOpen}
        />
      </EditTemplate>
    </Box>
  )
}

export default SingleUser
